import * as Linking from 'expo-linking';
import { type Href, router, useLocalSearchParams } from 'expo-router';
import { useState } from 'react';
import { ActivityIndicator, Platform, ScrollView, StyleSheet, View } from 'react-native';
import { BackButton } from '../../../../../src/components/primitives/BackButton';
import { Badge } from '../../../../../src/components/primitives/Badge';
import { Button } from '../../../../../src/components/primitives/Button';
import { HStack, VStack } from '../../../../../src/components/primitives/Stack';
import { Body, Caption, Heading2, Heading4 } from '../../../../../src/components/primitives/Text';
import { useAuth } from '../../../../../src/contexts/AuthContext';
import { useTranslation } from '../../../../../src/contexts/TranslationContext';
import { CallMethodSheet } from '../../../../../src/features/leads/components/CallMethodSheet';
import { AudioPlayer } from '../../../../../src/features/voice-agents/components/AudioPlayer';
import { OutcomeBadge } from '../../../../../src/features/voice-agents/components/OutcomeBadge';
import { StructuredSummary } from '../../../../../src/features/voice-agents/components/StructuredSummary';
import { TranscriptPanel } from '../../../../../src/features/voice-agents/components/TranscriptPanel';
import {
  useRetryVoiceCall,
  useVoiceCall,
  useVoiceCallAudio,
} from '../../../../../src/features/voice-agents/hooks';
import type {
  VoiceCallOutcome,
  VoiceCallTranscriptEntry,
} from '../../../../../src/features/voice-agents/types';
import { colors } from '../../../../../src/theme/colors';
import { parseOriginHref } from '../../../../../src/utils/originHref';

type BadgeColor = 'cyan' | 'green' | 'red' | 'amber' | 'purple' | 'blue' | 'neutral';

const STATUS_COLORS: Record<string, BadgeColor> = {
  scheduled: 'amber',
  dialing: 'cyan',
  completed: 'green',
  no_answer: 'neutral',
  technical_failure: 'red',
  cancelled: 'neutral',
};

const STATUS_LABEL_KEYS: Record<string, string> = {
  scheduled: 'scheduled',
  context_loaded: 'contextLoaded',
  dialing: 'dialing',
  detecting: 'detecting',
  disclosure: 'disclosure',
  discovery: 'discovery',
  handling: 'handling',
  cta: 'cta',
  closing: 'closing',
  post_processing: 'postProcessing',
  completed: 'completed',
  no_answer: 'noAnswer',
  voicemail: 'voicemail',
  hung_up_by_lead: 'hungUpByLead',
  technical_failure: 'technicalFailure',
  cancelled: 'cancelled',
};

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <HStack spacing="sm" style={styles.infoRow}>
      <Caption style={styles.infoLabel}>{label}</Caption>
      <Caption style={styles.infoValue} numberOfLines={1}>
        {value}
      </Caption>
    </HStack>
  );
}

function AudioSection({
  callId,
  organizationId,
  durationSeconds,
}: {
  callId: string;
  organizationId: string;
  durationSeconds?: number | null;
}) {
  const { t } = useTranslation();
  const { data: audio, isLoading } = useVoiceCallAudio(callId, organizationId);

  if (isLoading) {
    return (
      <View
        style={styles.audioSection}
        accessible
        accessibilityLabel={t('voiceAgents.callDetail.loadingAudio')}
      >
        <ActivityIndicator size="small" color={colors.primary.main} />
      </View>
    );
  }

  const audioUrl = audio?.url;
  if (!audioUrl) {
    return (
      <View
        style={styles.audioSection}
        accessible
        accessibilityLabel={t('voiceAgents.callDetail.audioUnavailableA11y')}
      >
        <Caption style={styles.noAudio}>{t('voiceAgents.callDetail.audioUnavailable')}</Caption>
      </View>
    );
  }

  const expiresLabel = audio.expiresAt
    ? `${t('voiceAgents.callDetail.signedUrlExpires')} ${new Date(audio.expiresAt).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}`
    : '';

  return (
    <View style={styles.audioSection}>
      <AudioPlayer url={audioUrl} durationSeconds={durationSeconds ?? null} testID="audio-player" />
      <HStack spacing="md" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="outline"
          size="sm"
          onPress={() => Linking.openURL(audioUrl)}
          accessibilityLabel={t('voiceAgents.callDetail.downloadAudioA11y')}
        >
          {t('voiceAgents.callDetail.downloadAudio')}
        </Button>
        {expiresLabel ? (
          <Caption style={styles.expiresLabel} accessibilityLabel={expiresLabel}>
            {expiresLabel}
          </Caption>
        ) : null}
      </HStack>
    </View>
  );
}

export default function CallDetailScreen() {
  const { t } = useTranslation();
  // `from` is set by whoever opened this screen (FLOSEAI-658). It is untrusted
  // address-bar input, so it is validated before it can drive navigation; an
  // absent or rejected value leaves the calls list as the fallback.
  const { callId, from } = useLocalSearchParams<{ callId: string; from?: string }>();
  const originHref = parseOriginHref(from) as Href | undefined;
  const { currentOrganization, backendUser } = useAuth();
  const orgId = currentOrganization?.id ?? '';

  const { data: call, isLoading } = useVoiceCall(callId ?? '', orgId);
  const retryCall = useRetryVoiceCall();
  // FLOSEAI-666 — starting a call from this screen must ask how, exactly like
  // the lead-detail header does. This button used to dial outright, and because
  // it gave no visible feedback QA pressed it twice and two calls went to the
  // same lead in the same minute.
  const [showCallMethod, setShowCallMethod] = useState(false);

  if (isLoading) {
    return (
      <View
        style={styles.center}
        accessible
        accessibilityLabel={t('voiceAgents.callDetail.loadingDetail')}
      >
        <ActivityIndicator color={colors.primary.main} size="large" />
      </View>
    );
  }

  if (!call) {
    return (
      <View style={styles.center}>
        <Body style={{ color: colors.text.secondary }}>{t('voiceAgents.callDetail.notFound')}</Body>
      </View>
    );
  }

  const statusColor = (STATUS_COLORS[call.status] as BadgeColor) ?? 'neutral';
  const statusLabelKey = STATUS_LABEL_KEYS[call.status];
  const statusLabel = statusLabelKey
    ? t(`voiceAgents.callDetail.status.${statusLabelKey}`)
    : call.status;

  const canRetry = call.status === 'no_answer' || call.status === 'technical_failure';
  const isAdmin = backendUser?.role === 'admin' || backendUser?.role === 'manager';

  const durationStr = call.durationSeconds
    ? `${Math.floor(call.durationSeconds / 60)}m ${call.durationSeconds % 60}s`
    : '—';

  const transcriptEntries = (call.transcriptEntries ?? []) as VoiceCallTranscriptEntry[];

  return (
    <ScrollView
      style={styles.root}
      contentContainerStyle={styles.content}
      accessibilityLabel={t('voiceAgents.callDetail.screenA11y')}
    >
      <HStack style={styles.topBar} spacing="md">
        <BackButton
          accessibilityLabel={t('common.actions.goBack')}
          tooltip={t('common.actions.goBack')}
          originHref={originHref}
          fallbackHref="/voice-agents/calls"
        />
        <Heading2 style={styles.title} accessibilityRole="header" numberOfLines={1}>
          {t('voiceAgents.callDetail.title')}
        </Heading2>
        <HStack spacing="sm">
          <Badge
            color={statusColor}
            size="sm"
            accessibilityLabel={`${t('voiceAgents.callDetail.statusPrefix')} ${statusLabel}`}
          >
            {statusLabel}
          </Badge>
          <OutcomeBadge outcome={call.outcome as VoiceCallOutcome | undefined} />
        </HStack>
      </HStack>

      <VStack spacing="lg">
        <VStack style={styles.card} spacing="md">
          <Heading4 style={styles.sectionTitle} accessibilityRole="header">
            {t('voiceAgents.callDetail.audioSection')}
          </Heading4>
          <AudioSection
            callId={call.id}
            organizationId={orgId}
            durationSeconds={call.durationSeconds}
          />
        </VStack>

        <VStack style={styles.card} spacing="md">
          <Heading4 style={styles.sectionTitle}>{t('voiceAgents.callDetail.transcript')}</Heading4>
          <View style={{ height: 320 }}>
            <TranscriptPanel entries={transcriptEntries} mode="replay" />
          </View>
        </VStack>

        {call.summary ? (
          <VStack style={styles.card} spacing="sm">
            <Heading4 style={styles.sectionTitle} accessibilityRole="header">
              {t('voiceAgents.callDetail.summary')}
            </Heading4>
            <StructuredSummary summary={call.summary} />
          </VStack>
        ) : null}

        <VStack style={styles.card} spacing="sm">
          <Heading4 style={styles.sectionTitle}>{t('voiceAgents.callDetail.details')}</Heading4>
          <InfoRow label={t('voiceAgents.callDetail.from')} value={call.fromNumber} />
          <InfoRow label={t('voiceAgents.callDetail.to')} value={call.toNumber} />
          <InfoRow label={t('voiceAgents.callDetail.country')} value={call.toCountry} />
          <InfoRow label={t('voiceAgents.callDetail.duration')} value={durationStr} />
          <InfoRow
            label={t('voiceAgents.callDetail.startedAt')}
            value={call.startedAt ? new Date(call.startedAt).toLocaleString('es-CO') : null}
          />
          <InfoRow
            label={t('voiceAgents.callDetail.endedAt')}
            value={call.endedAt ? new Date(call.endedAt).toLocaleString('es-CO') : null}
          />
          <InfoRow label={t('voiceAgents.callDetail.agent')} value={call.voiceAgent?.name} />
          <InfoRow
            label={t('voiceAgents.callDetail.agentVersion')}
            value={call.voiceAgentVersion?.toString()}
          />
          <InfoRow
            label={t('voiceAgents.callDetail.latencyP50')}
            value={call.turnTakingP50Ms?.toString()}
          />
          <InfoRow
            label={t('voiceAgents.callDetail.latencyP95')}
            value={call.turnTakingP95Ms?.toString()}
          />
          {call.costTelephonyUsd != null ? (
            <InfoRow
              label={t('voiceAgents.callDetail.telephonyCost')}
              value={`$${Number(call.costTelephonyUsd).toFixed(4)} USD`}
            />
          ) : null}
          {call.costConversationUsd != null ? (
            <InfoRow
              label={t('voiceAgents.callDetail.conversationCost')}
              value={`$${Number(call.costConversationUsd).toFixed(4)} USD`}
            />
          ) : null}
        </VStack>

        <HStack spacing="md" style={styles.actionRow}>
          {isAdmin ? (
            <Button
              variant="outline"
              size="sm"
              onPress={() => router.push(`/voice-agents/calls/${call.id}/replay`)}
              accessibilityLabel={t('voiceAgents.callDetail.viewReplayA11y')}
            >
              {t('voiceAgents.callDetail.viewReplay')}
            </Button>
          ) : null}
          {canRetry ? (
            <Button
              variant="primary"
              size="sm"
              onPress={() => setShowCallMethod(true)}
              accessibilityLabel={t('voiceAgents.callDetail.retryA11y')}
              accessibilityState={{ busy: retryCall.isPending, disabled: retryCall.isPending }}
              disabled={retryCall.isPending}
            >
              {retryCall.isPending
                ? t('voiceAgents.callDetail.retrying')
                : t('voiceAgents.callDetail.retry')}
            </Button>
          ) : null}
        </HStack>
      </VStack>

      {call.lead ? (
        <CallMethodSheet
          visible={showCallMethod}
          onClose={() => setShowCallMethod(false)}
          lead={call.lead}
          onCallHuman={(phone) => {
            setShowCallMethod(false);
            const dialTarget = `tel:${phone.replace(/[^\d+]/g, '')}`;
            if (Platform.OS === 'web') {
              window.open(dialTarget, '_self');
            } else {
              Linking.openURL(dialTarget).catch(() => undefined);
            }
          }}
          onCallAgent={() => {
            setShowCallMethod(false);
            retryCall.mutate({ id: call.id, organizationId: orgId });
          }}
        />
      ) : null}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  content: {
    padding: 16,
    paddingBottom: 40,
    gap: 20,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    backgroundColor: colors.background.primary,
  },
  topBar: {
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 4,
  },
  title: {
    color: colors.text.primary,
    flex: 1,
  },
  card: {
    backgroundColor: colors.background.card,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.border.subtle,
  },
  sectionTitle: {
    color: colors.text.primary,
    fontWeight: '600',
  },
  audioSection: {
    gap: 8,
  },
  noAudio: {
    color: colors.text.tertiary,
  },
  expiresLabel: {
    color: colors.text.tertiary,
    fontSize: 11,
    marginTop: 4,
  },
  summaryText: {
    color: colors.text.body,
    lineHeight: 22,
  },
  infoRow: {
    alignItems: 'flex-start',
  },
  infoLabel: {
    color: colors.text.tertiary,
    width: 120,
    flexShrink: 0,
  },
  infoValue: {
    color: colors.text.secondary,
    flex: 1,
  },
  actionRow: {
    justifyContent: 'flex-end',
    paddingTop: 4,
  },
});
